import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageLayout, PageHero } from '~/common/components';
import { NavCategoryV3, NavCategoryV3Handle } from './components/nav-category';
import { VehicleCategoryV3 } from './components/vehicle-category';

import { ShowroomCategoryV3 } from './store';
import { PricingQueueProvider } from '~/common/contexts/PricingQueueContext';
import styles from './index.module.scss';

interface Showroomv3Props {
  cdnBasePath: string;
  heading: string;
  headingText: string;
  showroomCategories: ShowroomCategoryV3[];
  heroBackground: string;
}

export const Showroomv3: React.FC<Showroomv3Props> = ({
  heading,
  headingText,
  showroomCategories,
  heroBackground,
}) => {
  const [activeSelector, setActiveSelector] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const navRef = useRef<NavCategoryV3Handle>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const historyTimeout = useRef<NodeJS.Timeout | null>(null);

  const sortedCategories = showroomCategories ? [...showroomCategories] : [];

  const updateWindowHistory = useCallback((selector: string) => {
    const newUrl = `${window.location.pathname}#${selector}${window.location.search}`;
    window.history.replaceState(null, window.document.title, newUrl);
  }, []);

  const handleScrollTo = useCallback(
    (selector: string) => {
      const element = categoryRefs.current[selector];
      if (element) {
        window.scrollTo({
          behavior: 'smooth',
          top: element.offsetTop - 85,
        });
        updateWindowHistory(selector);
        setActiveSelector(selector);
      }
    },
    [updateWindowHistory]
  );

  const handleViewEnter = useCallback(
    (selector: string) => {
      setActiveSelector(selector);

      if (historyTimeout.current) {
        clearTimeout(historyTimeout.current);
      }
      historyTimeout.current = setTimeout(() => {
        updateWindowHistory(selector);
      }, 300);
    },
    [updateWindowHistory]
  );

  useEffect(() => {
    setIsMounted(true);
    return () => {
      if (historyTimeout.current) clearTimeout(historyTimeout.current);
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => handleScrollTo(hash), 500);
    }
  }, [handleScrollTo]);

  return (
    <PageLayout>
      <PricingQueueProvider>
        <PageHero src={heroBackground} />

        <NavCategoryV3
          ref={navRef}
          categories={(sortedCategories || []).map((cat) => ({
            id: cat.id,
            selector: cat.categorySelector,
            label: cat.name,
            icon: cat.navigationIcon,
          }))}
          activeSelector={activeSelector}
          onScrollTo={handleScrollTo}
        />

        <div className={styles.headerSection}>
          <div className="container">
            <div
              className={styles.pageTitle}
              dangerouslySetInnerHTML={{ __html: heading }}
            />
            {headingText && (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{
                  __html: headingText,
                }}
              />
            )}
          </div>
        </div>

        <div className={styles.categoriesContainer}>
          {(sortedCategories || []).map((category, index) => {
            if (index > 0 && !isMounted) {
              return (
                <div
                  key={category.id}
                  style={{
                    minHeight: '320px',
                    backgroundColor: index % 2 === 0 ? '#EDEDED' : '#E5E5E5',
                  }}
                />
              );
            }

            return (
              <VehicleCategoryV3
                key={category.id}
                category={category}
                ref={(el) =>
                  (categoryRefs.current[category.categorySelector] = el)
                }
                backgroundColor={index % 2 === 0 ? '#EDEDED' : '#E5E5E5'}
                onViewEnter={handleViewEnter}
              />
            );
          })}
        </div>
      </PricingQueueProvider>
    </PageLayout>
  );
};
